/**
 * Wallet QBit SDK - API 客户端
 * QBit 钱包集成后端接口封装
 * 
 * 使用 BaseApiClient 基类简化代码
 */

import { BaseApiClient } from '@quantabit/sdk-config';

/**
 * QBit 钱包 API 客户端
 */
export class WalletQBitApiClient extends BaseApiClient {
  constructor(config = {}) {
    super('/wallet/quantabit', config);
  }

  // ============ 钱包初始化 ============

  /**
   * 初始化 QBit 钱包
   * @param {Object} options - 初始化选项
   */
  async initWallet(options = {}) {
    return this.post('/init', options);
  }

  /**
   * 获取 QBit 钱包状态
   */
  async getWalletStatus() {
    return this.get('/status');
  }

  /**
   * 绑定 QBit 账户
   * @param {Object} credentials - QBit 凭证
   */
  async bindAccount(credentials) {
    return this.post('/bind', credentials);
  }

  /**
   * 解绑 QBit 账户
   */
  async unbindAccount() {
    return this.post('/unbind');
  }

  // ============ 资产查询 ============

  /**
   * 获取资产总览
   */
  async getPortfolio() {
    return this.get('/portfolio');
  }

  /**
   * 获取资产余额
   * @param {string} assetId - 资产 ID
   */
  async getBalance(assetId) {
    return this.get(`/balance/${assetId}`);
  }

  /**
   * 获取所有余额
   */
  async getAllBalances() {
    return this.get('/balances');
  }

  /**
   * 获取资产价值历史
   * @param {Object} params - 查询参数
   */
  async getValueHistory(params = {}) {
    return this.get('/portfolio/history', params);
  }

  // ============ 投资操作 ============

  /**
   * 获取投资产品列表
   * @param {Object} params - 查询参数
   */
  async getInvestProducts(params = {}) {
    return this.get('/invest/products', params);
  }

  /**
   * 获取产品详情
   * @param {string} productId - 产品 ID
   */
  async getProduct(productId) {
    return this.get(`/invest/products/${productId}`);
  }

  /**
   * 申购产品
   * @param {string} productId - 产品 ID
   * @param {string} amount - 金额
   */
  async subscribe(productId, amount) {
    return this.post(`/invest/products/${productId}/subscribe`, { amount });
  }

  /**
   * 赎回产品
   * @param {string} productId - 产品 ID
   * @param {string} amount - 金额
   */
  async redeem(productId, amount) {
    return this.post(`/invest/products/${productId}/redeem`, { amount });
  }

  /**
   * 获取我的投资
   */
  async getMyInvestments() {
    return this.get('/invest/my');
  }

  // ============ 交易操作 ============

  /**
   * 获取充值地址
   * @param {string} assetId - 资产 ID
   * @param {string} chain - 链类型
   */
  async getDepositAddress(assetId, chain) {
    return this.get('/deposit/address', { asset_id: assetId, chain });
  }

  /**
   * 提现
   * @param {Object} withdrawData - 提现数据
   */
  async withdraw(withdrawData) {
    return this.post('/withdraw', withdrawData);
  }

  /**
   * 内部转账
   * @param {Object} transferData - 转账数据
   */
  async internalTransfer(transferData) {
    return this.post('/transfer/internal', transferData);
  }

  /**
   * 获取交易历史
   * @param {Object} params - 查询参数
   */
  async getTransactionHistory(params = {}) {
    return this.get('/transactions', params);
  }

  /**
   * 获取交易详情
   * @param {string} txId - 交易 ID
   */
  async getTransaction(txId) {
    return this.get(`/transactions/${txId}`);
  }

  // ============ 收益查询 ============

  /**
   * 获取收益统计
   * @param {Object} params - 查询参数
   */
  async getEarnings(params = {}) {
    return this.get('/earnings', params);
  }

  /**
   * 获取收益明细
   * @param {Object} params - 查询参数
   */
  async getEarningDetails(params = {}) {
    return this.get('/earnings/details', params);
  }

  /**
   * 获取 APY 信息
   * @param {string} productId - 产品 ID
   */
  async getApy(productId) {
    return this.get(`/invest/products/${productId}/apy`);
  }

  // ============ DeFi 操作 ============

  /**
   * 获取 DeFi 协议列表
   */
  async getDefiProtocols() {
    return this.get('/defi/protocols');
  }

  /**
   * 获取流动性池
   * @param {Object} params - 查询参数
   */
  async getLiquidityPools(params = {}) {
    return this.get('/defi/pools', params);
  }

  /**
   * 添加流动性
   * @param {Object} liquidityData - 流动性数据
   */
  async addLiquidity(liquidityData) {
    return this.post('/defi/add-liquidity', liquidityData);
  }

  /**
   * 移除流动性
   * @param {Object} liquidityData - 流动性数据
   */
  async removeLiquidity(liquidityData) {
    return this.post('/defi/remove-liquidity', liquidityData);
  }

  /**
   * 质押
   * @param {Object} stakeData - 质押数据
   */
  async stake(stakeData) {
    return this.post('/defi/stake', stakeData);
  }

  /**
   * 解除质押
   * @param {Object} unstakeData - 解除质押数据
   */
  async unstake(unstakeData) {
    return this.post('/defi/unstake', unstakeData);
  }

  // ============ 通知设置 ============

  /**
   * 获取通知设置
   */
  async getNotificationSettings() {
    return this.get('/settings/notifications');
  }

  /**
   * 更新通知设置
   * @param {Object} settings - 通知设置
   */
  async updateNotificationSettings(settings) {
    return this.put('/settings/notifications', settings);
  }

  /**
   * 设置价格提醒
   * @param {Object} alert - 提醒配置
   */
  async setPriceAlert(alert) {
    return this.post('/alerts/price', alert);
  }

  /**
   * 获取我的提醒
   */
  async getMyAlerts() {
    return this.get('/alerts');
  }
}

// 创建默认实例
export const walletQBitApi = new WalletQBitApiClient();



export default walletQBitApi;
